import React from 'react';
import { Cookie, Printer } from 'lucide-react';
import { CONTACT_EMAILS } from '../constants/contact';

/**
 * CookiePolicy - Cookie Policy page for GDPR/CCPA compliance (Issue #1310)
 *
 * Provides detailed information about:
 * - All cookies used by the platform
 * - Cookie purposes and durations
 * - Essential vs. non-essential classification
 * - How to manage cookie preferences
 */
export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Cookie className="w-8 h-8 text-secondary" />
              <h1 className="text-3xl font-bold text-gray-900">Cookie Policy</h1>
            </div>
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 px-4 py-2 text-secondary hover:text-primary-dark hover:bg-secondary-light/50 rounded-lg transition-colors print:hidden"
              aria-label="Print this document"
            >
              <Printer className="w-5 h-5" />
              <span className="hidden sm:inline">Print</span>
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-2">
            © {new Date().getFullYear()} Local Artist Fairs. All rights reserved.
          </p>
          <p className="text-sm text-gray-600 mb-8">
            Last Updated: March 1, 2026
          </p>

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">What Are Cookies?</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website.
              They help websites remember your preferences, improve your experience, and provide analytics about how the site is used.
            </p>
            <p className="text-gray-700 leading-relaxed">
              This Cookie Policy explains what cookies we use, why we use them, and how you can control them.
            </p>
          </section>

          {/* Types of Cookies */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Types of Cookies We Use</h2>

            {/* Essential Cookies */}
            <div className="mb-6 border-l-4 border-green-500 pl-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                1. Essential Cookies (Strictly Necessary)
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                These cookies are necessary for the platform to function and cannot be disabled. They do not require consent under GDPR.
              </p>

              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Cookie Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Purpose</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Duration</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Party</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="px-4 py-3 text-sm text-gray-700 font-mono">csrf_token</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        CSRF protection for secure state-changing requests
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">1 hour</td>
                      <td className="px-4 py-3 text-sm text-gray-700">First-party</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-3 text-sm text-gray-700 font-mono">session_id</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        User authentication and session management
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">Session</td>
                      <td className="px-4 py-3 text-sm text-gray-700">First-party</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="mb-6 border-l-4 border-dusty-teal pl-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                2. Analytics Cookies (Requires Consent)
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                These cookies help us understand how users interact with our platform. They collect anonymized data
                about page views, features used, and user behavior. <strong className="text-primary-dark">These cookies require your explicit consent under GDPR.</strong>
              </p>

              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Cookie Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Purpose</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Duration</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Party</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="px-4 py-3 text-sm text-gray-700 font-mono">_ga</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Google Analytics - Distinguish unique users
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">2 years</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Third-party (Google)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-3 text-sm text-gray-700 font-mono">_gid</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Google Analytics - Distinguish unique users
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">24 hours</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Third-party (Google)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-3 text-sm text-gray-700 font-mono">_gat_*</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Google Analytics - Throttle request rate
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">1 minute</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Third-party (Google)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-4 p-4 bg-moonbone border border-teal-200 rounded-lg">
                <p className="text-sm text-ink">
                  <strong>Note:</strong> You can manage your analytics preferences in your{' '}
                  <a href="/profile" className="underline hover:text-ink">user profile settings</a>.
                  Your consent choices are stored in localStorage and respected across sessions.
                </p>
              </div>
            </div>

            {/* Functional Cookies */}
            <div className="mb-6 border-l-4 border-purple-500 pl-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                3. Functional Cookies (Third-Party Services)
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                These cookies are set by third-party services we use to provide essential functionality like error monitoring
                and payment processing.
              </p>

              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Service</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Purpose</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Duration</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Party</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="px-4 py-3 text-sm text-gray-700 font-semibold">Sentry</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Error monitoring, session replay, debugging
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">Session</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Third-party (Sentry)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-3 text-sm text-gray-700 font-semibold">Stripe</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Payment processing, fraud detection
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">Varies</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Third-party (Stripe)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm text-purple-800">
                  <strong>Note:</strong> These services have their own cookie policies. See our{' '}
                  <a href="/privacy" className="underline hover:text-purple-900">Privacy Policy</a>
                  {' '}for links to their privacy policies.
                </p>
              </div>
            </div>
          </section>

          {/* How We Use Cookies */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Cookies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use cookies for the following purposes:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li><strong>Security:</strong> Protect against cross-site request forgery (CSRF) attacks</li>
              <li><strong>Authentication:</strong> Keep you logged in across pages</li>
              <li><strong>Analytics:</strong> Understand usage patterns and improve the platform (with consent)</li>
              <li><strong>Error Monitoring:</strong> Detect and fix technical issues</li>
              <li><strong>Payment Processing:</strong> Securely process transactions via Stripe</li>
            </ul>
          </section>

          {/* Managing Cookies */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How to Manage Cookies</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Cookie Consent Banner</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you first visit our platform, you'll see a cookie consent banner. You can choose to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6 ml-4">
              <li><strong>Accept All:</strong> Enable all cookies including analytics</li>
              <li><strong>Reject Non-Essential:</strong> Only enable essential cookies required for the platform to function</li>
              <li><strong>Customize:</strong> Choose which cookie categories to enable</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">User Profile Settings</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You can change your cookie preferences at any time in your{' '}
              <a href="/profile" className="text-secondary hover:text-primary-dark underline">user profile settings</a>.
              Look for the "Analytics & Privacy" section.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Browser Settings</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You can also control cookies through your browser settings:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
              <li><strong>Firefox:</strong> Preferences → Privacy & Security → Cookies and Site Data</li>
              <li><strong>Safari:</strong> Preferences → Privacy → Cookies and website data</li>
              <li><strong>Edge:</strong> Settings → Cookies and site permissions → Manage and delete cookies</li>
            </ul>

            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Warning:</strong> Blocking essential cookies may prevent you from using certain features of our platform,
                such as logging in or making payments.
              </p>
            </div>
          </section>

          {/* Do Not Track */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Do Not Track (DNT)</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We automatically respect the "Do Not Track" (DNT) browser setting. If DNT is enabled in your browser,
              analytics cookies will be disabled regardless of your consent choices.
            </p>
            <p className="text-gray-700 leading-relaxed">
              To enable DNT, check your browser's privacy settings.
            </p>
          </section>

          {/* Third-Party Cookies */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Third-Party Cookies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Some cookies are set by third-party services (Google Analytics, Sentry, Stripe). We do not control these cookies.
              For information about how these services use cookies, please see their privacy policies:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>
                <strong>Google Analytics:</strong>{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-primary-dark underline">
                  Google Privacy Policy
                </a>
              </li>
              <li>
                <strong>Sentry:</strong>{' '}
                <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-primary-dark underline">
                  Sentry Privacy Policy
                </a>
              </li>
              <li>
                <strong>Stripe:</strong>{' '}
                <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-primary-dark underline">
                  Stripe Privacy Policy
                </a>
              </li>
            </ul>
          </section>

          {/* Changes to This Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Cookie Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Cookie Policy from time to time. We will notify you of any changes by updating the
              "Last Updated" date at the top of this page.
            </p>
          </section>

          {/* Contact Us */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have questions about our use of cookies, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700 mb-2">
                <strong>Email:</strong>{' '}
                <a href={`mailto:${CONTACT_EMAILS.privacy}`} className="text-secondary hover:text-primary-dark underline">
                  {CONTACT_EMAILS.privacy}
                </a>
              </p>
              <p className="text-gray-700">
                <strong>Feedback Form:</strong>{' '}
                <a href="/feedback" className="text-secondary hover:text-primary-dark underline">
                  Submit Feedback
                </a>
              </p>
            </div>
          </section>

          {/* Footer Links */}
          <div className="pt-6 border-t border-gray-200">
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <a href="/privacy" className="hover:text-secondary underline">Privacy Policy</a>
              <span>|</span>
              <a href="/terms" className="hover:text-secondary underline">Terms of Service</a>
              <span>|</span>
              <a href="/" className="hover:text-secondary underline">Back to Home</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
